import { useState } from "react";
import { motion } from "framer-motion";
import { Navigation } from "./Index/components/Navigation";
import { Footer } from "./Index/components/Footer";
import { useLanguage } from "@/contexts/LanguageContext";
import { translations } from "@/translations";
import { Snowflake, Camera, BookOpen, Palette, Clock, CreditCard, Calendar, Gift } from "lucide-react";
import { OpenHouseBanner } from "@/components/OpenHouseBanner";
import ChristmasCampRegistrationForm from "@/components/ChristmasCampRegistrationForm";
import { OptimizedPicture } from "@/components/OptimizedPicture";
import { usePageMeta } from "@/hooks/usePageMeta";

const META: Record<string, { title: string; description: string }> = {
  es: {
    title: "Campamento de Navidad | Gándara International School",
    description: "Campamento de Navidad en Gondomar para niños de 3 a 10 años. Actividades creativas, naturaleza y diversión en Gándara.",
  },
  en: {
    title: "Christmas Camp | Gándara International School",
    description: "Christmas camp in Gondomar for children aged 3-10. Creative activities, nature and fun at Gándara.",
  },
  pt: {
    title: "Acampamento de Natal | Gándara International School",
    description: "Acampamento de Natal em Gondomar para crianças de 3 a 10 anos. Atividades criativas, natureza e diversão na Gándara.",
  },
  fr: {
    title: "Camp de Noël | Gándara International School",
    description: "Camp de Noël à Gondomar pour enfants de 3 à 10 ans. Activités créatives, nature et amusement à Gándara.",
  },
};

const ChristmasCamp = () => {
  const { language } = useLanguage();
  const t = translations[language].christmasCamp;
  const [isBannerVisible, setIsBannerVisible] = useState(true);
  const meta = META[language] || META.es;
  usePageMeta({ title: meta.title, description: meta.description });

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      <OpenHouseBanner onVisibilityChange={setIsBannerVisible} />
      
      <div className={`${isBannerVisible ? 'pt-[120px]' : 'pt-20'} pb-20`}>
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="max-w-4xl mx-auto"
          >
            {/* Header with Christmas theme */}
            <div className="text-center mb-12">
              <div className="flex justify-center gap-4 mb-6">
                <Snowflake className="w-12 h-12 text-red-500 animate-pulse" />
                <Gift className="w-16 h-16 text-green-600" />
                <Snowflake className="w-12 h-12 text-red-500 animate-pulse" />
              </div>
              <h1 className="text-5xl font-light text-primary mb-6">
                {t.title}
              </h1>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                {t.subtitle}
              </p>
            </div>

            {/* Gallery Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
              className="bg-card rounded-2xl p-8 shadow-lg mb-8 overflow-hidden border-2 border-red-500/20"
            >
              <h2 className="text-3xl font-light text-primary mb-6 flex items-center gap-3">
                <Camera className="w-8 h-8 text-red-500" />
                {t.gallery.title}
              </h2>
              
              {/* Main image */}
              <div className="relative aspect-video rounded-xl overflow-hidden mb-4 bg-muted">
                <OptimizedPicture 
                  src={t.gallery.images[0].src} 
                  alt={t.gallery.images[0].alt} 
                  width={800}
                  height={450}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover transition-opacity duration-300"
                />
              </div>
              
              <p className="text-sm text-muted-foreground text-center mt-4">
                {t.gallery.caption}
              </p>
            </motion.div>

            {/* Description Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
              className="bg-card rounded-2xl p-8 shadow-lg mb-8 border-2 border-green-600/20"
            >
              <h2 className="text-3xl font-light text-primary mb-6 flex items-center gap-3">
                <BookOpen className="w-8 h-8 text-green-600" />
                {t.description.title}
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                {t.description.content1}
              </p>
              <p className="text-muted-foreground leading-relaxed">
                {t.description.content2}
              </p>
            </motion.div>

            {/* Activities Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              viewport={{ once: true }}
              className="bg-card rounded-2xl p-8 shadow-lg mb-8 border-2 border-red-500/20"
            >
              <h2 className="text-3xl font-light text-primary mb-6 flex items-center gap-3">
                <Palette className="w-8 h-8 text-red-500" />
                {t.activities.title}
              </h2>
              <ul className="space-y-3">
                {t.activities.list.map((item: string, index: number) => (
                  <li key={index} className="flex items-start gap-3">
                    <span className="text-green-600 mt-1">✓</span>
                    <span className="text-muted-foreground">{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Schedule Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              viewport={{ once: true }}
              className="bg-card rounded-2xl p-8 shadow-lg mb-8 border-2 border-green-600/20"
            >
              <h2 className="text-3xl font-light text-primary mb-6 flex items-center gap-3">
                <Clock className="w-8 h-8 text-green-600" />
                {t.schedule.title}
              </h2>
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <Calendar className="w-5 h-5 text-red-500" />
                  <span className="text-foreground font-medium">{t.schedule.dates}</span>
                </div>
                <p className="text-muted-foreground text-sm ml-8">{t.schedule.note}</p>
                <div className="flex items-center gap-3">
                  <Clock className="w-5 h-5 text-green-600" />
                  <span className="text-muted-foreground">{t.schedule.hours}</span>
                </div>
                <div className="flex items-center gap-3">
                  <Clock className="w-5 h-5 text-red-500" />
                  <span className="text-muted-foreground">{t.schedule.extended}</span>
                </div>
                <div className="mt-4 p-3 bg-muted rounded-lg">
                  <span className="text-foreground font-medium">{t.schedule.ages}</span>
                </div>
              </div>
            </motion.div>

            {/* Prices Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.5 }}
              viewport={{ once: true }}
              className="bg-card rounded-2xl p-8 shadow-lg mb-8 border-2 border-red-500/20"
            >
              <h2 className="text-3xl font-light text-primary mb-6 flex items-center gap-3">
                <CreditCard className="w-8 h-8 text-red-500" />
                {t.prices.title}
              </h2>
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead>
                    <tr className="border-b border-red-500/20">
                      <th className="py-3 px-2"></th>
                      <th className="py-3 px-2 text-foreground">{t.prices.oneWeek}</th>
                      <th className="py-3 px-2 text-foreground">{t.prices.complete}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-green-600/20">
                      <td className="py-3 px-2 font-medium text-foreground">{t.prices.members}</td>
                      <td className="py-3 px-2 text-muted-foreground">80€</td>
                      <td className="py-3 px-2 text-muted-foreground">200€</td>
                    </tr>
                    <tr>
                      <td className="py-3 px-2 font-medium text-foreground">{t.prices.nonMembers}</td>
                      <td className="py-3 px-2 text-muted-foreground">100€</td>
                      <td className="py-3 px-2 text-muted-foreground">225€</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </motion.div>

            {/* Registration Form */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.6 }}
              viewport={{ once: true }}
              className="mb-8"
              id="inscripcion"
            >
              <div className="text-center mb-8">
                <div className="flex justify-center gap-2 mb-4">
                  <Snowflake className="w-6 h-6 text-red-500" />
                  <Gift className="w-6 h-6 text-green-600" />
                  <Snowflake className="w-6 h-6 text-red-500" />
                </div>
                <h2 className="text-2xl font-light text-primary">
                  {t.contact.title}
                </h2>
                <p className="text-muted-foreground mt-2">
                  {language === 'es' ? "¡Plazas limitadas!" : 
                   language === 'pt' ? "Vagas limitadas!" : 
                   language === 'fr' ? "Places limitées !" : 
                   "Limited spots!"}
                </p>
              </div>
              <ChristmasCampRegistrationForm />
            </motion.div>
          </motion.div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ChristmasCamp;
